import { Request, Response } from 'express';
import { injectable, inject } from 'inversify';
import { BoundedContextApplicationService } from '../../../../application/services/BoundedContextApplicationService';
import { CreateBoundedContextRequestSchema, UpdateBoundedContextRequestSchema, GetBoundedContextsQuerySchema } from '../dto/BoundedContextRequestDTOs';
import { validateRequest, validateBoundedContextParams, validateDomainContextParams, createErrorResponse } from '../middleware/ValidationMiddleware';
import { 
  createSuccessResponse, 
  createErrorResponse as createStandardErrorResponse, 
  BUSINESS_ACTIONS, 
  DOMAIN_NAMES 
} from '@swoft/core';

/**
 * Bounded Context Controller
 * 
 * RESTful API controller for bounded context operations.
 * Binds ONLY to BoundedContextApplicationService - no direct domain access.
 * 
 * Enforces Clean Architecture by maintaining strict API boundaries.
 */
@injectable()
export class BoundedContextController {
    constructor(
        @inject('BoundedContextApplicationService')
        private readonly boundedContextApplicationService: BoundedContextApplicationService
    ) {}

    /**
     * Create a new bounded context
     * POST /bounded-contexts
     */
    async createBoundedContext(req: Request, res: Response): Promise<void> {
        try {
            // Validate request body
            const validatedRequest = CreateBoundedContextRequestSchema.parse(req.body);
            
            const result = await this.boundedContextApplicationService.createBoundedContext(validatedRequest);
            
            const response = createSuccessResponse(
                result,
                BUSINESS_ACTIONS.DDD.CREATE_BOUNDED_CONTEXT,
                DOMAIN_NAMES.DDD,
                'command'
            );
            
            res.status(201).json(response);
        } catch (error: any) {
            const errorResponse = createStandardErrorResponse(
                'BOUNDED_CONTEXT_CREATE_FAILED',
                'Failed to create bounded context',
                BUSINESS_ACTIONS.DDD.CREATE_BOUNDED_CONTEXT,
                DOMAIN_NAMES.DDD,
                'command',
                error instanceof Error ? error.message : 'Unknown error'
            );
            res.status(400).json(errorResponse);
        }
    }

    /**
     * Get all bounded contexts with optional filtering
     * GET /bounded-contexts
     */
    async getBoundedContexts(req: Request, res: Response): Promise<void> {
        try {
            // Validate query parameters
            const validatedQuery = GetBoundedContextsQuerySchema.parse(req.query);
            
            const result = await this.boundedContextApplicationService.getBoundedContexts(validatedQuery);
            
            const response = createSuccessResponse(
                {
                    contexts: result,
                    total: result.length
                },
                BUSINESS_ACTIONS.DDD.LIST_BOUNDED_CONTEXTS,
                DOMAIN_NAMES.DDD,
                'query'
            );
            
            res.status(200).json(response);
        } catch (error: any) {
            const errorResponse = createStandardErrorResponse(
                'BOUNDED_CONTEXT_LIST_FAILED',
                'Failed to retrieve bounded contexts',
                BUSINESS_ACTIONS.DDD.LIST_BOUNDED_CONTEXTS,
                DOMAIN_NAMES.DDD,
                'query',
                error instanceof Error ? error.message : 'Unknown error'
            );
            res.status(400).json(errorResponse);
        }
    }

    /**
     * Get a specific bounded context by ID
     * GET /bounded-contexts/:contextId
     */
    async getBoundedContext(req: Request, res: Response): Promise<void> {
        try {
            const { contextId } = req.params;
            
            const result = await this.boundedContextApplicationService.getBoundedContext(contextId);
            
            const response = createSuccessResponse(
                result,
                BUSINESS_ACTIONS.DDD.LIST_BOUNDED_CONTEXTS,
                DOMAIN_NAMES.DDD,
                'query'
            );
            
            res.status(200).json(response);
        } catch (error: any) {
            const statusCode = error.message?.includes('not found') ? 404 : 400;
            const errorResponse = createStandardErrorResponse(
                'BOUNDED_CONTEXT_GET_FAILED',
                'Failed to retrieve bounded context',
                BUSINESS_ACTIONS.DDD.LIST_BOUNDED_CONTEXTS,
                DOMAIN_NAMES.DDD,
                'query',
                error instanceof Error ? error.message : 'Unknown error'
            );
            res.status(statusCode).json(errorResponse);
        }
    }

    /**
     * Get bounded contexts for a specific domain
     * GET /domains/:domainId/bounded-contexts
     */
    async getBoundedContextsByDomain(req: Request, res: Response): Promise<void> {
        try {
            const { domainId } = req.params;
            const validatedQuery = GetBoundedContextsQuerySchema.parse({
                ...req.query,
                domainId
            });
            
            const result = await this.boundedContextApplicationService.getBoundedContexts(validatedQuery);
            
            const response = createSuccessResponse(
                {
                    contexts: result,
                    total: result.length
                },
                BUSINESS_ACTIONS.DDD.LIST_BOUNDED_CONTEXTS,
                DOMAIN_NAMES.DDD,
                'query'
            );
            
            res.status(200).json(response);
        } catch (error: any) {
            const errorResponse = createStandardErrorResponse(
                'DOMAIN_BOUNDED_CONTEXT_LIST_FAILED',
                'Failed to retrieve domain bounded contexts',
                BUSINESS_ACTIONS.DDD.LIST_BOUNDED_CONTEXTS,
                DOMAIN_NAMES.DDD,
                'query',
                error instanceof Error ? error.message : 'Unknown error'
            );
            res.status(400).json(errorResponse);
        }
    }

    /**
     * Get context map showing relationships between bounded contexts
     * GET /context-map
     * GET /domains/:domainId/context-map
     */
    async getContextMap(req: Request, res: Response): Promise<void> {
        try {
            const { domainId } = req.params; // Optional - may be undefined for global context map
            
            const result = await this.boundedContextApplicationService.getContextMap(domainId);
            
            const response = createSuccessResponse(
                result,
                BUSINESS_ACTIONS.DDD.LIST_BOUNDED_CONTEXTS,
                DOMAIN_NAMES.DDD,
                'query'
            );
            
            res.status(200).json(response);
        } catch (error: any) {
            const errorResponse = createStandardErrorResponse(
                'CONTEXT_MAP_GET_FAILED',
                'Failed to retrieve context map',
                BUSINESS_ACTIONS.DDD.LIST_BOUNDED_CONTEXTS,
                DOMAIN_NAMES.DDD,
                'query',
                error instanceof Error ? error.message : 'Unknown error'
            );
            res.status(400).json(errorResponse);
        }
    }

    /**
     * Get middleware for bounded context parameter validation
     */
    static getValidationMiddleware(): any {
        return {
            validateBoundedContextParams,
            validateDomainContextParams,
            validateCreateBoundedContext: validateRequest({ body: CreateBoundedContextRequestSchema }),
            validateUpdateBoundedContext: validateRequest({ body: UpdateBoundedContextRequestSchema }),
            validateGetBoundedContextsQuery: validateRequest({ query: GetBoundedContextsQuerySchema })
        };
    }
}